/**
 * Forgot-password form (email reset).
 */
import * as React from 'react';
import { Input } from '@/components/forms/Input';
import { Button } from '@/components/forms/Button';
import { isValidEmail } from '@/utils/auth-validation-utils';
import { Mail, ArrowLeft, KeyRound, Send } from 'lucide-react';
import { getEmailFieldError, getEmailFieldState } from '@/components/auth/auth-utils';

/** Props for {@link ResetPasswordForm}. */
interface ResetPasswordFormProps {
  email: string;
  isLoading: boolean;
  emailTouched: boolean;
  error?: string;
  onEmailChange: (value: string) => void;
  onEmailBlur: () => void;
  onSubmit: React.SubmitEventHandler<HTMLFormElement>;
  onBackToLogin: () => void;
}

/** Forgot-password card: email field, send-link, and back to login. */
export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  email,
  isLoading,
  emailTouched,
  error,
  onEmailChange,
  onEmailBlur,
  onSubmit,
  onBackToLogin,
}) => {
  function handleEmailInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onEmailChange(e.target.value);
  }

  return (
    <div className="reset-view">
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative">
            <div className="absolute inset-0 opacity-20 blur-xl rounded-full login-reset-key-blur" />
            <div className="relative p-3 rounded-full login-reset-key-badge">
              <KeyRound className="h-6 w-6 login-reset-key-icon" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Mot de passe oublié ?</h1>
            <p className="text-sm text-muted-foreground">Réinitialisez votre mot de passe</p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          Saisissez votre adresse email. Si un compte existe, vous recevrez un lien pour activer
          votre compte ou réinitialiser votre mot de passe.
        </p>

        <form onSubmit={onSubmit} className="space-y-6">
          <Input
            type="email"
            label="Adresse email"
            placeholder="exemple@email.com"
            value={email}
            onChange={handleEmailInputChange}
            onBlur={onEmailBlur}
            error={getEmailFieldError(email, emailTouched, error)}
            icon={Mail}
            iconPosition="left"
            state={getEmailFieldState(email, emailTouched)}
            disabled={isLoading}
            required
            autoFocus
          />

          <div className="space-y-3">
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading || !email.trim() || !isValidEmail(email)}
              fullWidth
              variant="primary"
              size="lg"
              icon={Send}
              iconPosition="left"
            >
              Envoyer le lien
            </Button>

            <Button type="button" onClick={onBackToLogin} fullWidth variant="ghost" size="lg">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au login
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
